/* eslint-disable no-underscore-dangle */
import {
  namespace,
  ACTION_TYPE,
  LOAD_STATUS,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import { storeCashBackUserId } from '@skyscanner-internal/landing-pages-shared-ui/src/hotels-utils/services/backend-gateway/cash-back-user-id';
import { getQueryParam } from '@skyscanner-internal/landing-pages-shared-ui/src/hotels-utils/services/url/url-params';
import logMiniEventHelper from '@skyscanner-internal/landing-pages-shared-ui/src/mini-event/logMiniEventHelper';
import { getAcceptLanguageFirstLang } from '@skyscanner-internal/landing-pages-shared-ui/src/utils';
import { GLOBAL, afterDomContentLoaded } from 'saddlebag-browser';
import logger from 'saddlebag-logger';
import observer from 'saddlebag-observer';

import '@skyscanner-internal/backpack-web/bpk-stylesheets/base';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/base.css';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/font';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/larken';
import '@skyscanner-internal/backpack-web/bpk-component-horizontal-nav/src/BpkHorizontalNav.module.css';
import '@skyscanner-internal/backpack-web/bpk-component-horizontal-nav/src/BpkHorizontalNavItem.module.css';

import './public-path';
import '@skyscanner-internal/landing-pages-shared-ui/src/home.scss';
import './ingested.scss';
import { loadExternalScripts } from './scriptLoader';

const $window = GLOBAL.getWindow();
const clientSideContext = $window.__internal;
const rootComponent = `${namespace}.root`;

const initializeSponsoredAgent = async () => {
  const { default: persistSponsoredAgent } = await import(
    /* webpackMode: "eager" */ './sponsored-agent-handler'
  );
  persistSponsoredAgent();
};

const afterPageLoaded = async (data) => {
  const {
    consentIdServiceUrl = '/g/purpose-scoped-id-service/identifiers/consent-id',
    enableConsentIdService = false,
    enableKetchCMP = false,
    enableKetchPsidSync = false,
    enableLegacyConsentSync = false,
    enablePurposeScopedIdExistingGDPRUsers = false,
    enablePurposeScopedIdNonRegulatedMarkets = false,
    enablePurposeScopedIdService = false,
    ketchConsentCookieName = '_swb_consent_',
    ketchScriptUrl = 'https://eu.ketchcdn.com/web/v3/config/skyscanner_eu/web_production/boot.js',
    ketchTCFStorageKey = '_ketch_tcf_tcdata_v1_',
    pageType,
    purposeScopedIdServiceKetchCmpUrl = '/g/purpose-scoped-id-service/identifiers/refresh',
    purposeScopedIdServiceLegacyCmpUrl = '/g/purpose-scoped-id-service/identifiers/refresh-legacy',
    useBpkCardRestyle,
    useStrevdaRuntime,
  } = data;

  const { initTrackingPreferences } =
    await import('saddlebag-tracking-preferences');
  initTrackingPreferences({
    consentIdServiceUrl,
    enableConsentIdService,
    enableKetchCMP,
    enableKetchPsidSync,
    enableLegacyConsentSync,
    enablePurposeScopedIdExistingGDPRUsers,
    enablePurposeScopedIdNonRegulatedMarkets,
    enablePurposeScopedIdService,
    consumer: 'falcon',
    market: data.culture?.market,
    ketchScriptUrl,
    ketchConsentCookieName,
    ketchTCFStorageKey,
    purposeScopedIdServiceKetchCmpUrl,
    purposeScopedIdServiceLegacyCmpUrl,
  });

  const { default: hydrate } = await import('./hydrate');
  await hydrate();
  observer.publish('grappler-track', {
    eventName: 'PageLoaded',
    fullSchemaName: 'landing_pages.PageLoaded',
    message: {
      microsite: 'FALCON',
      page_type: pageType,
      accept_language: (data.culture && data.culture.acceptLanguage) || '',
      accept_language_first_lang:
        getAcceptLanguageFirstLang(
          (data.culture && data.culture.acceptLanguage) || null,
        ) || '',
      suggested_market: (data.culture && data.culture.suggestedMarket) || '',
    },
    isMiniEvent: true,
    consentRequired: true,
    microsite: { name: namespace },
  });

  const cashBackUserId = getQueryParam(
    $window.location.href,
    'cashback_user_id',
  );
  if (cashBackUserId) {
    const market = data.culture?.market;
    storeCashBackUserId(cashBackUserId, market);
  }

  const { name, variant } = data.distilledExperiment;
  if (name && variant) {
    observer.publish('tracking-event', {
      ga: [
        {
          category: `${data.pageType}-results-distilled-experiment`,
          action: variant,
          label: `${name} Results Variant ${variant.toUpperCase()}`,
        },
      ],
    });
  }

  await Promise.all([
    useStrevdaRuntime
      ? import(
          /* webpackChunkName: "StrevdaRuntime" */ '@skyscanner-internal/strevda-runtime'
        ).then((strevdaRuntime) => {
          const { strevdaRuntimeProps } = $window.__internal;
          delete $window.__internal.strevdaRuntimeProps;
          strevdaRuntime.init({ ...strevdaRuntimeProps });
        })
      : Promise.resolve(),

    import(/* webpackMode: "eager" */ './gtmClient').then(
      ({ default: loadGtmClient }) => loadGtmClient(),
    ),

    useBpkCardRestyle ? import('./bpk-card-restyle.scss') : Promise.resolve(),
  ]);

  const { default: customNewRelicAttributes } = await import(
    /* webpackMode: "eager" */ './custom-new-relic-attributes'
  );
  customNewRelicAttributes($window);
};

(async () => {
  try {
    await loadExternalScripts();
  } catch (missing) {
    logger.logError(missing, { component: rootComponent });
  }

  try {
    await initializeSponsoredAgent();
    afterDomContentLoaded(() =>
      afterPageLoaded(clientSideContext).catch((err) => {
        logger.logError(err, { component: rootComponent });
      }),
    );
    logMiniEventHelper({
      action_type: ACTION_TYPE.PAGE_LOADED,
      load_status: LOAD_STATUS.LOADED,
    });
  } catch (err) {
    logger.logError(err, { component: rootComponent });
  }
})();
